import { useEffect, useState } from 'react';
import {
  usePracticeShortQuery,
  useProviderCsvTemplateDownloadMutation,
  useProvidersCsvImportMutation,
} from '@common/cache';
import { SelectChangeEvent } from '@mui/material';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';

export const useImportProviderModal = (handleClose: () => void) => {
  const { data: practiceOptions = [] } = usePracticeShortQuery({});
  const {
    mutateAsync: templateMutateAsync,
    data: csvTemplate,
    isPending,
  } = useProviderCsvTemplateDownloadMutation();
  const {
    mutateAsync: importMutateAsync,
    error,
    reset,
    isPending: isImportPending,
  } = useProvidersCsvImportMutation();
  const [practiceId, setPracticeId] = useState('');
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (csvTemplate) {
      const url = URL.createObjectURL(csvTemplate);

      const a = document.createElement('a');
      a.href = url;
      a.download = `provider template`;

      function handleOnDownload() {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', handleOnDownload);
        }, 150);
      }

      a.addEventListener('click', handleOnDownload, false);
      a.click();
    }
  }, [csvTemplate]);

  useEffect(() => {
    if (practiceOptions.length > 0) {
      setPracticeId(practiceOptions[0].id);
    }
  }, [practiceOptions]);

  const handleChangePracticeId = (event: SelectChangeEvent) => {
    setPracticeId(event.target.value);
  };

  const handleChangeFile = (newFile: File | null) => {
    setFile(newFile);
    reset();
  };

  const handleCloseAction = () => {
    handleClose();
    setFile(null);
  };

  const handleDownloadCSVTemplate = async () => {
    const loadingToast = toast.loading('Downloading CSV template...', {
      isLoading: true,
      autoClose: 500,
    });

    try {
      await templateMutateAsync(practiceId);
      toast.success('Downloaded successfully.');
    } catch {
      toast.error('Failed to download');
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  const handleImportCsvFile = async () => {
    const loadingToast = toast.loading('Importing CSV file...', {
      isLoading: true,
      autoClose: 500,
    });

    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      await importMutateAsync({
        file: formData,
        record_id: practiceId,
      });
      toast.success('Imported successfully.');
    } catch {
      toast.error('Something went wrong.');
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  return {
    practiceOptions: practiceOptions.map(o => ({ value: o.id, label: o.name })),
    practiceId,
    handleChangePracticeId,
    handleDownloadCSVTemplate,
    isTemplateBtnDisabled: isPending,
    handleImportCsvFile,
    handleChangeFile,
    importError: error ? formatApiError(error).split(', ') : [],
    file,
    handleCloseAction,
    isImportPending,
  };
};
